import { Checkbox } from '../shared/components/Checkbox'
import {
    FormInput,
    FormInputLang,
    FormRichText,
    FormSelect,
    FormTextarea,
    FormUpload,
} from '../shared/components/Fieldset'

export const FormFieldRenderer = ({
    field,
    onChange,
    value,
    error,
    readOnly = false,
    options,
    groupIndex = null,
    allValues = {},
}) => {
    const handleChange = val => onChange(field.name, val)

    // If field has a prerequisite and it’s not truthy in allValues, don’t render
    if (field.prerequisite && !allValues?.[field.prerequisite]) {
        return null
    }

    switch (field.type) {
        case 'text':
        case 'date':
        case 'datetime':
        case 'time':
        case 'email':
        case 'number':
        case 'password':
            return (
                <FormInput
                    type={field.type}
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    required={field.required}
                    onChange={e => handleChange(e.target.value)}
                    error={error}
                    value={value || ''}
                    readOnly={readOnly}
                />
            )
        case 'select':
            return (
                <FormSelect
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    options={options || field.options}
                    defaultValue={field.defaultValue || field.options[0]?.value}
                    description={field.description}
                    required={field.required}
                    onChange={e => handleChange(e.target.value)}
                    error={error}
                    value={value || ''}
                    readOnly={readOnly}
                />
            )
        case 'richtext':
            return (
                <FormRichText
                    key={field.name}
                    label={field.label}
                    description={field.description}
                    required={field.required}
                    error={error}
                    value={value || ''}
                    onChange={val => handleChange(val)}
                    readOnly={readOnly}
                />
            )

        case 'textarea':
            return (
                <FormTextarea
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    description={field.description}
                    rows={field.rows || 3}
                    required={field.required}
                    onChange={e => handleChange(e.target.value)}
                    error={error}
                    value={value || ''}
                    readOnly={readOnly}
                />
            )
        case 'checkbox':
            return (
                <Checkbox
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    onChange={val => handleChange(val)}
                    checked={value === true || value === 1 || value === '1' || value === 'true'}
                    error={error}
                    required={field.required}
                    readOnly={readOnly}
                    // disabled={readOnly}
                />
            )
        case 'checkbox-group':
            return (
                <div key={field.name} className='mb-4'>
                    <label className='text-sm font-medium text-gray-700'>
                        {field.label} {field.required && <span className='text-red-500'>*</span>}
                    </label>
                    <div className='mt-2 space-y-2'>
                        {field.options.map(option => (
                            <Checkbox
                                key={option}
                                name={`${field.name}-${option}`}
                                label={option}
                                onChange={isChecked => {
                                    const currentValues = Array.isArray(value) ? value : []
                                    const updatedValues = isChecked
                                        ? [...currentValues, option]
                                        : currentValues.filter(v => v !== option)
                                    handleChange(updatedValues)
                                }}
                                checked={value?.includes(option)}
                                disabled={readOnly}
                                className='capitalize'
                            />
                        ))}
                    </div>
                    {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
                </div>
            )
        case 'upload':
            return (
                <FormUpload
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    required={field.required}
                    onChange={handleChange}
                    initialUrl={value}
                    error={error}
                    readOnly={readOnly}
                />
            )
        case 'multilang':
            return (
                <FormInputLang
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    required={field.required}
                    onChange={onChange} // need to be passed directly, else it wont work
                    error={error}
                    values={allValues}
                    readOnly={readOnly}
                    isTextarea={field.isTextarea || false}
                    isRTF={field.isRTF || false}
                    groupIndex={groupIndex}
                />
            )
        case 'group':
            const isArrayGroup = !!field.multiple
            const emptyGroup = generateEmptyGroupValue(field.fields)
            const groupValues = isArrayGroup
                ? Array.isArray(value) && value.length > 0
                    ? value
                    : [emptyGroup]
                : [value || emptyGroup]

            // Get the error for this group
            const groupError = error

            return (
                <div key={field.name} className='p-4 mb-6 border border-gray-200 rounded'>
                    <label className='font-medium text-gray-700 text-md'>
                        {field.label} {field.required && <span className='text-red-500'>*</span>}
                    </label>

                    {/* Show group-level error (e.g., "At least 1 required") */}
                    {typeof groupError === 'string' && (
                        <p className='mt-1 text-sm text-red-500'>{groupError}</p>
                    )}

                    {groupValues.map((groupValue, index) => {
                        // Get errors for this specific group item
                        let itemErrors = {}

                        if (groupError && typeof groupError === 'object') {
                            if (isArrayGroup) {
                                // For multiple groups: groupError is {0: {...}, 1: {...}}
                                itemErrors = groupError[index] || {}
                            } else if (index === 0) {
                                // For single group: groupError is {'field1': 'error', 'field2': 'error'}
                                itemErrors = groupError
                            }
                        }

                        return (
                            <div
                                key={index}
                                className={`mb-6 grid gap-x-4 grid-cols-1 md:grid-cols-2 items-start ${
                                    isArrayGroup &&
                                    groupValues.length > 1 &&
                                    index !== groupValues.length - 1
                                        ? 'border-b border-gray-100'
                                        : ''
                                }`}
                            >
                                {field.fields.map(subField => {
                                    // Handle multilang fields specially
                                    if (subField.type === 'multilang') {
                                        // Collect all errors for this multilang field
                                        const multilangErrors = {}

                                        // Check for base field error (English)
                                        if (itemErrors[subField.name]) {
                                            multilangErrors.en = itemErrors[subField.name]
                                        }

                                        // Check for _ar field error (Arabic)
                                        if (itemErrors[`${subField.name}_ar`]) {
                                            multilangErrors.ar = itemErrors[`${subField.name}_ar`]
                                        }

                                        return (
                                            <div key={subField.name}>
                                                <FormInputLang
                                                    name={subField.name}
                                                    label={subField.label}
                                                    required={subField.required}
                                                    onChange={(subName, val, langSuffix) => {
                                                        let updated
                                                        const fullName =
                                                            subName + (langSuffix || '')
                                                        if (isArrayGroup) {
                                                            updated = [...groupValues]
                                                            updated[index] = {
                                                                ...updated[index],
                                                                [fullName]: val,
                                                            }
                                                            handleChange(updated)
                                                        } else {
                                                            updated = {
                                                                ...groupValue,
                                                                [fullName]: val,
                                                            }
                                                            handleChange(updated)
                                                        }
                                                    }}
                                                    error={
                                                        Object.keys(multilangErrors).length > 0
                                                            ? multilangErrors
                                                            : null
                                                    }
                                                    values={groupValue}
                                                    readOnly={readOnly}
                                                    isTextarea={subField.isTextarea || false}
                                                    groupIndex={index}
                                                />
                                            </div>
                                        )
                                    } else {
                                        // Regular fields
                                        const subFieldError = itemErrors[subField.name]

                                        return (
                                            <div
                                                key={subField.name}
                                                className={
                                                    subField.type === 'textarea'
                                                        ? 'md:col-span-2'
                                                        : ''
                                                }
                                            >
                                                <FormFieldRenderer
                                                    field={subField}
                                                    value={groupValue?.[subField.name]}
                                                    onChange={(subName, val) => {
                                                        let updated
                                                        if (isArrayGroup) {
                                                            updated = [...groupValues]
                                                            updated[index][subName] = val
                                                            handleChange(updated)
                                                        } else {
                                                            updated = {
                                                                ...groupValue,
                                                                [subName]: val,
                                                            }
                                                            handleChange(updated)
                                                        }
                                                    }}
                                                    error={subFieldError}
                                                    readOnly={readOnly}
                                                    groupIndex={index}
                                                    allValues={allValues}
                                                />
                                            </div>
                                        )
                                    }
                                })}

                                {isArrayGroup && !readOnly && (
                                    <div className='flex items-center gap-4 mt-2'>
                                        <button
                                            type='button'
                                            onClick={() => {
                                                const newGroups = [...groupValues]
                                                newGroups.splice(index + 1, 0, emptyGroup)
                                                handleChange(newGroups)
                                            }}
                                            className='text-sm text-yellow-500 hover:text-yellow-600'
                                        >
                                            + Add
                                        </button>

                                        {groupValues.length > 1 && (
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    const newGroups = [...groupValues]
                                                    newGroups.splice(index, 1)
                                                    handleChange(newGroups)
                                                }}
                                                className='text-sm text-red-500 hover:text-red-600'
                                            >
                                                − Remove
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )

        default:
            return null
    }
}

export const generateEmptyValues = fields => {
    return fields.reduce((values, field) => {
        // if (field.type === 'group') {
        //   // Handle group fields
        //   if (field.multiple) {
        //     values[field.name] = [generateEmptyGroupValue(field.fields)];
        //   } else {
        //     values[field.name] = generateEmptyGroupValue(field.fields);
        //   }
        // }
        // else
        if (field.type === 'checkbox') {
            // Default to true if the checkbox name is 'is_active', otherwise false
            values[field.name] = field.name === 'is_active' ? true : false
        }
        // else if (field.type === 'multilang') {
        //   values[field.name] = '';       // English version
        //   values[field.name + '_ar'] = ''; // Arabic version
        // }
        // else {
        //   values[field.name] = ''; // Default empty string for other fields
        // }
        return values
    }, {})
}

export const generateEmptyGroupValue = fields => {
    const obj = {}
    for (const field of fields) {
        if (field.type === 'checkbox') {
            obj[field.name] = false
        } else if (field.type === 'group') {
            obj[field.name] = field.multiple ? [] : generateEmptyGroupValue(field.fields)
        } else {
            obj[field.name] = ''
        }
    }
    return obj
}
